// src/pages/AdminDatabaseManager.js
import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  deleteField,
  addDoc,
  setDoc,
  onSnapshot
} from 'firebase/firestore';
import AdminSidebar from '../components/AdminSidebar';
import AdminHeader from '../components/AdminHeader';
import './AdminDashboard.css';

const AdminDatabaseManager = () => {
  const [data, setData] = useState([]);
  const [collectionName, setCollectionName] = useState('restaurants');
  const [loading, setLoading] = useState(true);
  const [newField, setNewField] = useState({ name: '', value: '' });
  const [dynamicCollections, setDynamicCollections] = useState([]);

  // قائمة بكل المجموعات في Firestore
  const collectionsList = [
    'restaurants',
    'drivers',
    'admins',
    'customers',
    'orders',
    'notifications',
    'driverActivities',
    'driverMovements',
    'driver_locations',
    'driver_notifications',
    'driver_messages',
    'chats',
    'web_notifications',
    'transactions',
    'admin_messages',
    'system',
    '_connection_test_',
    'restaurant_balances'
  ];

  // ترجمة أسماء المجموعات إلى العربية
  const collectionsLabels = {
    restaurants: 'المطاعم',
    drivers: 'السائقين',
    admins: 'الأدمن',
    customers: 'العملاء',
    orders: 'الطلبات',
    notifications: 'الإشعارات',
    driverActivities: 'نشاط السائقين',
    driverMovements: 'تحركات السائقين',
    driver_locations: 'مواقع السائقين',
    driver_notifications: 'إشعارات السائقين',
    driver_messages: 'رسائل السائقين',
    chats: 'الدردشات',
    web_notifications: 'إشعارات الويب',
    transactions: 'المعاملات',
    admin_messages: 'رسائل الأدمن',
    system: 'النظام',
    _connection_test_: 'اختبار الاتصال',
    restaurant_balances: 'رصيد المطاعم'
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, collectionName));
      setData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('خطأ في جلب البيانات:', e);
      alert('❌ حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  }, [collectionName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    const pw = prompt('أدخل كلمة مرور الأدمن لحذف هذا العنصر:');
    if (pw !== 'admin123') return alert('❌ كلمة مرور خاطئة');
    try {
      await deleteDoc(doc(db, collectionName, id));
      alert('✅ تم حذف العنصر');
      fetchData();
    } catch (e) {
      console.error('خطأ أثناء الحذف:', e);
      alert('❌ حدث خطأ أثناء الحذف');
    }
  };

  const handleFieldUpdate = async (id, field, value) => {
    try {
      await updateDoc(doc(db, collectionName, id), { [field]: value });
      alert('✅ تم تعديل الحقل');
      fetchData();
    } catch (e) {
      console.error('خطأ أثناء التعديل:', e);
      alert('❌ حدث خطأ أثناء التعديل');
    }
  };

  const handleFieldDelete = async (id, field) => {
    const pw = prompt('أدخل كلمة مرور الأدمن لحذف هذا الحقل:');
    if (pw !== 'admin123') return alert('❌ كلمة مرور خاطئة');
    try {
      await updateDoc(doc(db, collectionName, id), { [field]: deleteField() });
      alert('✅ تم حذف الحقل');
      fetchData();
    } catch (e) {
      console.error('خطأ أثناء حذف الحقل:', e);
      alert('❌ حدث خطأ أثناء حذف الحقل');
    }
  };

  const handleClearCollection = async () => {
    if (!window.confirm(`هل أنت متأكد من حذف جميع ${collectionsLabels[collectionName] || collectionName}?`)) return;
    const pw = prompt('أدخل كلمة مرور الأدمن لحذف جميع البيانات:');
    if (pw !== 'admin123') return alert('❌ كلمة مرور خاطئة');
    setLoading(true);
    try {
      for (const item of data) {
        await deleteDoc(doc(db, collectionName, item.id));
      }
      alert(`✅ تم حذف جميع ${collectionsLabels[collectionName] || collectionName}`);
      fetchData();
    } catch (e) {
      console.error('خطأ أثناء حذف المجموعة:', e);
      alert('❌ حدث خطأ أثناء حذف جميع البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = async (id) => {
    try {
      if (!newField.name || !newField.value) {
        alert('❌ يجب إدخال اسم الحقل وقيمته');
        return;
      }

      // تحويل القيمة إلى النوع المناسب
      let processedValue = newField.value;
      if (newField.value.toLowerCase() === 'true') processedValue = true;
      else if (newField.value.toLowerCase() === 'false') processedValue = false;
      else if (!isNaN(newField.value)) processedValue = Number(newField.value);
      else if (newField.value.startsWith('{') || newField.value.startsWith('[')) {
        try {
          processedValue = JSON.parse(newField.value);
        } catch (e) {
          // إذا فشل التحليل، نترك القيمة كما هي
        }
      }

      await updateDoc(doc(db, collectionName, id), {
        [newField.name]: processedValue
      });
      
      alert('✅ تم إضافة الحقل بنجاح');
      setNewField({ name: '', value: '' });
      fetchData();
    } catch (e) {
      console.error('خطأ أثناء إضافة الحقل:', e);
      alert('❌ حدث خطأ أثناء إضافة الحقل');
    }
  };

  const handleAddNewDocument = async () => {
    try {
      if (!newField.name || !newField.value) {
        alert('❌ يجب إدخال اسم الحقل وقيمته على الأقل');
        return;
      }

      // تحويل القيمة إلى النوع المناسب
      let processedValue = newField.value;
      if (newField.value.toLowerCase() === 'true') processedValue = true;
      else if (newField.value.toLowerCase() === 'false') processedValue = false;
      else if (!isNaN(newField.value)) processedValue = Number(newField.value);
      else if (newField.value.startsWith('{') || newField.value.startsWith('[')) {
        try {
          processedValue = JSON.parse(newField.value);
        } catch (e) {
          // إذا فشل التحليل، نترك القيمة كما هي
        }
      }

      await addDoc(collection(db, collectionName), {
        [newField.name]: processedValue,
        createdAt: new Date().toISOString()
      });
      
      alert('✅ تم إضافة المستند بنجاح');
      setNewField({ name: '', value: '' });
      fetchData();
    } catch (e) {
      console.error('خطأ أثناء إضافة المستند:', e);
      alert('❌ حدث خطأ أثناء إضافة المستند');
    }
  };

  // دالة لجلب جميع المجموعات الموجودة في قاعدة البيانات
  const fetchAllCollections = useCallback(async () => {
    try {
      // استخدام onSnapshot لمراقبة التغييرات في المجموعات
      const unsubscribe = onSnapshot(collection(db, '_collections'), (snapshot) => {
        const collectionNames = snapshot.docs.map(doc => doc.id);
        // دمج المجموعات الثابتة مع المجموعات الديناميكية
        const allCollections = [...new Set([...collectionsList, ...collectionNames])];
        setDynamicCollections(allCollections);
      }, (error) => {
        console.error('خطأ في مراقبة المجموعات:', error);
      });

      // تنظيف المراقب عند إزالة المكون
      return () => unsubscribe();
    } catch (error) {
      console.error('خطأ في جلب المجموعات:', error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = fetchAllCollections();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [fetchAllCollections]);

  return (
    <div className="restaurant-dashboard">
      <AdminSidebar />
      <div className="restaurant-dashboard-content">
        <AdminHeader adminName="إدارة قاعدة البيانات" />

        <div style={{ 
          marginBottom: '20px',
          paddingBottom: '60px' // إضافة مسافة في الأسفل لتجنب تداخل المحتوى مع الفوتر
        }}>
          <label>اختر المجموعة: </label>
          <select
            value={collectionName}
            onChange={e => setCollectionName(e.target.value)}
          >
            {dynamicCollections.map(c => (
              <option key={c} value={c}>
                {collectionsLabels[c] || c}
              </option>
            ))}
          </select>
          <button
            onClick={handleClearCollection}
            style={{
              marginLeft: '10px',
              backgroundColor: '#e63946',
              color: '#fff',
              padding: '5px 10px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            🗑️ حذف كل {collectionsLabels[collectionName] || collectionName}
          </button>
        </div>

        {/* نموذج إضافة حقل جديد */}
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h3>إضافة حقل جديد</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <input
              type="text"
              placeholder="اسم الحقل"
              value={newField.name}
              onChange={e => setNewField(prev => ({ ...prev, name: e.target.value }))}
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ced4da' }}
            />
            <input
              type="text"
              placeholder="قيمة الحقل"
              value={newField.value}
              onChange={e => setNewField(prev => ({ ...prev, value: e.target.value }))}
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ced4da' }}
            />
            <button
              onClick={handleAddNewDocument}
              style={{
                backgroundColor: '#28a745',
                color: '#fff',
                padding: '5px 10px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              ➕ إضافة مستند جديد
            </button>
          </div>
          <small style={{ color: '#6c757d' }}>
  ملاحظة: يمكنك إدخال القيم كـ JSON (مثل: &#123;"key": "value"&#125;) أو كأرقام أو نصوص أو قيم منطقية (true/false)
</small>

        </div>

        {loading ? (
          <p>جاري تحميل البيانات...</p>
        ) : (
          data.map(item => (
            <div
              key={item.id}
              style={{
                border: '1px solid #ccc',
                padding: '10px',
                marginBottom: '15px',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <pre style={{ direction: 'ltr', color: '#000' }}>
                {JSON.stringify(item, null, 2)}
              </pre>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  onClick={() => handleDelete(item.id)}
                  style={{
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '4px',
                  }}
                >
                  🗑️ حذف العنصر
                </button>
                <button
                  onClick={() => {
                    const field = prompt('أدخل اسم الحقل للتعديل:');
                    if (!field) return;
                    const val = prompt('أدخل القيمة الجديدة:');
                    if (val == null) return;
                    handleFieldUpdate(item.id, field, val);
                  }}
                  style={{
                    backgroundColor: '#0d6efd',
                    color: '#fff',
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '4px',
                  }}
                >
                  ✏️ تعديل حقل
                </button>
                <button
                  onClick={() => {
                    const field = prompt('أدخل اسم الحقل للحذف:');
                    if (!field) return;
                    handleFieldDelete(item.id, field);
                  }}
                  style={{
                    backgroundColor: '#ffc107',
                    color: '#000',
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '4px',
                  }}
                >
                  ❌ حذف حقل
                </button>
                <button
                  onClick={() => handleAddField(item.id)}
                  style={{
                    backgroundColor: '#28a745',
                    color: '#fff',
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '4px',
                  }}
                >
                  ➕ إضافة حقل
                </button>
              </div>
            </div>
          ))
        )}

        {/* شريط الفوتر الثابت */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #dee2e6',
          padding: '10px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1000,
          marginRight: '250px' // لترك مساحة للسايدبار
        }}>
          <div style={{ color: '#6c757d' }}>
            عدد العناصر: {data.length}
          </div>
          <div style={{ color: '#6c757d' }}>
            المجموعة الحالية: {collectionsLabels[collectionName] || collectionName}
          </div>
          <div style={{ color: '#6c757d' }}>
            آخر تحديث: {new Date().toLocaleTimeString('ar-SA')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDatabaseManager;
